import { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import { type ServiceVariantFragment, formatPrice } from "@stepapp/core";
import type { V2CartServiceVariantsUpsert } from "@stepapp/core/apiTypes";
import { Block, Button, Divider, Typography } from "@stepapp/ui";

import { useTranslation } from "react-i18next";
import { AppStorage } from "../../services/AppStorage";
import QuantityActions from "./QuantityActions";

type ActionModalProps = {
  title: string;
  variants: ServiceVariantFragment[];
  closeModal: () => void;
  selectedAnswers?: V2CartServiceVariantsUpsert[];
  handleQuantityChange: (
    serviceId: string,
    id: string,
    quantity: number,
    singleVariant: boolean,
  ) => void;
  serviceId: string;
};

const ServiceActions = ({
  closeModal,
  selectedAnswers,
  handleQuantityChange,
  title,
  variants,
  serviceId,
}: ActionModalProps) => {
  const { t } = useTranslation();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));
  const price = useMemo(() => {
    return (
      selectedAnswers?.reduce((sum, { quantity, variantId }) => {
        const matchingOption = variants.find((option) => option.id === variantId);

        return matchingOption ? sum + (quantity || 0) * matchingOption.price : sum;
      }, 0) || 0
    );
  }, [selectedAnswers, variants]);

  const singleOption = variants.length === 1;

  const handleSelectQuantity = useCallback(
    (quantity: number, id: string) => {
      handleQuantityChange(serviceId, id, Math.max(0, quantity), singleOption);
    },
    [handleQuantityChange, serviceId, singleOption],
  );

  return (
    <>
      <Typography variant="newH2">{title}</Typography>

      <Block marginTop={4}>
        {variants?.map(({ description, price, id, title }, index) => (
          <Block key={id}>
            <Block alignCenter row>
              <Block flex={1}>
                <Typography variant="bodyBold">{`${title} (${description})`}</Typography>
                <Block marginTop={0.5}>
                  <Typography type="secondary" variant="paragraph">
                    {priceFormatter(price || 0)}
                  </Typography>
                </Block>
              </Block>

              <QuantityActions
                inModal
                onChange={(quantity) => {
                  handleSelectQuantity(quantity, id);
                }}
                quantity={selectedAnswers?.find((s) => s?.variantId === id)?.quantity || 0}
              />
            </Block>

            {variants.length !== index + 1 && <Divider />}
          </Block>
        ))}
      </Block>

      <Block marginTop={5.5}>
        <Typography type="secondary" variant="body">
          {title}
        </Typography>
      </Block>

      <Block row marginTop={4}>
        {price > 0 && (
          <Block flex={1} style={styles.priceContainer}>
            <Typography variant="newH2">{priceFormatter(price)}</Typography>
            <Typography variant="paragraph">{title}</Typography>
          </Block>
        )}
        <Block flex={1}>
          <Button label={t("common.continue")} onPress={closeModal} type="primary" />
        </Block>
      </Block>
    </>
  );
};

export default ServiceActions;

const styles = StyleSheet.create({
  priceContainer: {
    maxWidth: 218,
  },
});
